import { collection, getDocs, doc, getDoc, query, orderBy, where } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { getTeamMemberIds } from '../../../utils/teamUtils';

/**
 * Fetch draft_orders scoped by the caller's role.
 *
 * @param {{ role: string, agentId: string }} context - logged-in agent context
 * @returns {Promise<Array>}
 */
export const draftOrderService = {
  async getDraftOrders({ role, agentId }) {
    try {
      const ref = collection(db, 'draft_orders');
      let snapshots = [];

      if (role === 'admin') {
        // Admins see everything
        const q = query(ref, orderBy('created_at', 'desc'));
        const snap = await getDocs(q);
        snapshots.push(snap);

      } else if (role === 'teamLeader') {
        // Team leader sees own orders + each team member's orders.
        // draft_orders uses agent_id field (string) — query in batches of 10.
        const memberIds = await getTeamMemberIds(agentId);
        const allIds = [agentId, ...memberIds]; // include self

        for (let i = 0; i < allIds.length; i += 10) {
          const batch = allIds.slice(i, i + 10);
          const q = query(ref, where('agent_id', 'in', batch));
          snapshots.push(await getDocs(q));
        }

      } else {
        // All other roles see only their own orders
        const q = query(ref, where('agent_id', '==', agentId));
        const snap = await getDocs(q);
        snapshots.push(snap);
      }

      // Merge, deduplicate, sort descending by created_at
      const seen = new Set();
      const orders = [];
      for (const snap of snapshots) {
        snap.forEach((docSnap) => {
          if (!seen.has(docSnap.id)) {
            seen.add(docSnap.id);
            orders.push({ id: docSnap.id, ...docSnap.data() });
          }
        });
      }

      orders.sort((a, b) => {
        const da = new Date(a.created_at || a.createdAt || 0);
        const db_ = new Date(b.created_at || b.createdAt || 0);
        return db_ - da;
      });

      return orders;
    } catch (error) {
      console.error('Error fetching draft_orders:', error);
      throw error;
    }
  },

  async getDraftOrderById(orderId) {
    try {
      const docRef = doc(db, 'draft_orders', orderId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      return null;
    } catch (error) {
      console.error(`Error fetching draft order ${orderId}:`, error);
      throw error;
    }
  }
};

